import { safeGet, safeRemove, safeSet } from "@/components/onboarding/safe-storage";

export const SAVED_VIEWS_STORAGE_PREFIX = "upsider_saved_views";
export const SAVED_VIEWS_ANON_SCOPE = "anon";

export type SavedViewToolKey =
  | "optimizer-props"
  | "ev-sniper"
  | "odds-screen"
  | "whale-watch";

export type SavedView = {
  id: string;
  name: string;
  search: string;
  path?: string;
  createdAt: string;
};

type SaveViewInput = {
  name: string;
  search: string;
  path?: string | null;
};

function normalizeUserScope(userId?: string | null): string {
  const trimmed = typeof userId === "string" ? userId.trim() : "";
  return trimmed || SAVED_VIEWS_ANON_SCOPE;
}

export function getSavedViewsStorageKey(
  userId: string | null | undefined,
  toolKey: SavedViewToolKey,
): string {
  return `${SAVED_VIEWS_STORAGE_PREFIX}:${normalizeUserScope(userId)}:${toolKey}`;
}

export function normalizeSavedViewSearch(search: string): string {
  const trimmed = search.trim();
  if (trimmed === "" || trimmed === "?") return "";
  return trimmed.startsWith("?") ? trimmed : `?${trimmed}`;
}

function normalizeSavedViewPath(path: string | null | undefined): string | undefined {
  const trimmed = typeof path === "string" ? path.trim() : "";
  if (!trimmed) return undefined;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, " ") || "Untitled view";
}

function createSavedViewId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  return `view_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function coerceSavedView(value: unknown): SavedView | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (
    typeof raw.id !== "string" ||
    typeof raw.name !== "string" ||
    typeof raw.search !== "string" ||
    typeof raw.createdAt !== "string"
  ) {
    return null;
  }

  const id = raw.id.trim();
  if (!id) return null;

  const path = normalizeSavedViewPath(typeof raw.path === "string" ? raw.path : undefined);
  return {
    id,
    name: normalizeName(raw.name),
    search: normalizeSavedViewSearch(raw.search),
    ...(path ? { path } : {}),
    createdAt: raw.createdAt,
  };
}

function parseSavedViews(raw: string | null): SavedView[] {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(coerceSavedView)
      .filter((view): view is SavedView => view !== null);
  } catch {
    return [];
  }
}

function writeSavedViews(
  userId: string | null | undefined,
  toolKey: SavedViewToolKey,
  views: SavedView[],
): boolean {
  const key = getSavedViewsStorageKey(userId, toolKey);
  if (views.length === 0) {
    return safeRemove(key);
  }
  return safeSet(key, JSON.stringify(views));
}

export function listSavedViews(
  userId: string | null | undefined,
  toolKey: SavedViewToolKey,
): SavedView[] {
  return parseSavedViews(safeGet(getSavedViewsStorageKey(userId, toolKey)));
}

export function saveSavedView(
  userId: string | null | undefined,
  toolKey: SavedViewToolKey,
  input: SaveViewInput,
): SavedView | null {
  const path = normalizeSavedViewPath(input.path);
  const view: SavedView = {
    id: createSavedViewId(),
    name: normalizeName(input.name),
    search: normalizeSavedViewSearch(input.search),
    ...(path ? { path } : {}),
    createdAt: new Date().toISOString(),
  };
  const views = listSavedViews(userId, toolKey);
  return writeSavedViews(userId, toolKey, [view, ...views]) ? view : null;
}

export function renameSavedView(
  userId: string | null | undefined,
  toolKey: SavedViewToolKey,
  viewId: string,
  name: string,
): SavedView | null {
  const views = listSavedViews(userId, toolKey);
  let renamed: SavedView | null = null;
  const next = views.map((view) => {
    if (view.id !== viewId) return view;
    renamed = { ...view, name: normalizeName(name) };
    return renamed;
  });

  if (!renamed) return null;
  return writeSavedViews(userId, toolKey, next) ? renamed : null;
}

export function deleteSavedView(
  userId: string | null | undefined,
  toolKey: SavedViewToolKey,
  viewId: string,
): boolean {
  const views = listSavedViews(userId, toolKey);
  const next = views.filter((view) => view.id !== viewId);
  if (next.length === views.length) return false;
  return writeSavedViews(userId, toolKey, next);
}
